import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import
{
    MAT_DIALOG_DATA,
    MatDialogModule,
    MatDialogRef
} from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { Store } from "@ngxs/store";
import { BehaviorSubject, Observable, timer } from "rxjs";
import { EditDialogData } from "../../../interfaces";
import
{
    Note,
    Product,
    ProductService,
    UpdateNoteRequestModel,
    UpdateProductRequestModel,
    UpdateVersionRequestModel,
    Version
} from "../../../services/api";
import { clientTagValidator, versionNameValidator } from "../../../validators";
import { ProductApiStatusState, PutNote, PutProduct, PutVersion } from "../../product/state";
import { SubscriptionHandlerComponent } from "../subscription-handler/subscription-handler.component";

@Component({
    selector: 'app-edit-dialog',
    imports: [CommonModule, MatButtonModule, MatDialogModule, MatIconModule, MatInputModule, MatCardModule, FormsModule, ReactiveFormsModule],
    templateUrl: './edit-dialog.component.html',
    styleUrl: './edit-dialog.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
// Component to edit a string-value
export class EditDialogComponent extends SubscriptionHandlerComponent implements AfterViewInit
{
    editedValue$: Observable<boolean | null> = this.store.select(ProductApiStatusState.editedValue);

    productId$: BehaviorSubject<number> = new BehaviorSubject(0);

    // Edit button pressed flag
    editPressed: boolean = false;

    // Value for editing
    inputValue: string = "";

    product: Product;

    version: Version;

    note: Note;

    form: FormGroup;

    form2: FormGroup;

    constructor(@Inject(MAT_DIALOG_DATA) public data: EditDialogData,
        private store: Store,
        private productService: ProductService,
        private dialogRef: MatDialogRef<EditDialogComponent>)
    {
        super();
        // Setting string
        this.inputValue = data.value;
        // If-Statement for setting the VersionName- or ClientTag-Value
        if (this.data.property == "Version" && this.data.field === "Name")
        {
            this.form = new FormGroup({
                name: new FormControl<string>(data.value, {
                    validators: Validators.required,
                    asyncValidators: versionNameValidator(this.productService, this.productId$),
                    nonNullable: true
                },
                ),
            },
            );
        }
        else if (this.data.field === "Client Identifier")
        {
            this.form2 = new FormGroup({
                clientTag: new FormControl<string>(data.value, {
                    validators: [
                        Validators.pattern('^[\\w]{1,10}$'),
                        Validators.required,
                    ],
                    asyncValidators: clientTagValidator(this.productService),
                    nonNullable: true
                },
                ),
            },
            );
        }
    }

    ngAfterViewInit(): void
    {
        if (this.product)
            this.productId$.next(this.product.id!);
    }

    // Enter functionality of editing string-value
    enterEdit()
    {
        if (this.inputValue !== this.data.value && !this.editPressed)
        {
            this.editValue()
        }
    }

    enterEditVersionName()
    {
        if (this.form2.controls['name'].value !== this.data.value && !this.editPressed)
        {
            this.editValue()
        }
    }

    enterEditClientTag()
    {
        if (this.form2.controls['clientTag'].value !== this.data.value && !this.editPressed)
        {
            this.editValue()
        }
    }

    editValue()
    {
        this.editPressed = true;
        // If-Statement for editing every possible value
        if (this.data.property === "Product")
        {
            if (this.data.field === "Name")
            {
                let updatedProduct: UpdateProductRequestModel = {
                    id: this.product.id,
                    name: this.inputValue,
                }
                this.store.dispatch(new PutProduct(updatedProduct));
            } else if (this.data.field === "Client Identifier")
            {
                let updatedProduct: UpdateProductRequestModel = {
                    id: this.product.id,
                    clientTag: this.form2.controls['clientTag'].value
                }
                this.store.dispatch(new PutProduct(updatedProduct));
            }
        } else if (this.data.property == "Version")
        {
            if (this.data.field === "Name")
            {
                let updatedVersion: UpdateVersionRequestModel = {
                    id: this.version.id,
                    name: this.form.controls['name'].value
                }
                this.store.dispatch(new PutVersion(updatedVersion));
            }
        } else if (this.data.property == "Note")
        {
            if (this.data.field === "Jira ID")
            {
                let updatedNote: UpdateNoteRequestModel = {
                    id: this.note.id,
                    jiraId: this.inputValue,
                }
                this.store.dispatch(new PutNote(updatedNote));
            }
        }

        // Close the dialog 2 seconds after editing
        this.subscribeTo(this.editedValue$, x =>
        {
            if (x) timer(2000).subscribe(() => this.dialogRef.close(true));
        })
    }
}
